// ============================================================================
// PushPrimingHost (W7-B, E12) — kopfloser Ausloeser des RUECKFALLS.
// ----------------------------------------------------------------------------
// Aufbau nach dem Muster `ReviewPromptHost.tsx`: keine eigene Oberflaeche, nur
// Verkabelung (Auth, Wecker, Seitenzaehler, Aufraeumen beim Abmelden). Die
// Regel selbst steht in `pushPrimingRules.ts` und ist ohne DOM testbar.
//
// Reihenfolge nach E12: das EREIGNIS fragt zuerst (pushPrimingBus, gefeuert in
// der Chat-Seite bzw. beim fertigen Abenteuer). Dieser Host ist nur das Netz
// darunter — er feuert denselben Bus mit dem Grund 'fallback', wenn nach
// 5 Minuten Nutzung oder dem dritten Seitenaufruf noch nichts passiert ist.
//
// Der Empfaenger (StartupGateCoordinator) prueft die harten Grenzen erneut; hier
// werden sie nur vorgezogen, damit wir gar nicht erst Wecker stellen.
// ============================================================================

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { requestPushPriming } from '../native/pushPrimingBus';
import {
  isProactivePushEnabled,
  isStartupPushPromptDone,
} from '../native/pushPreferences';
import { ensureFirstExperience, hasFirstExperience } from '../tour/TourProvider';
import {
  countPushPrimingPageview,
  countsAsPushPrimingPageview,
  isPushPrimingQuietPath,
  PUSH_PRIMING_USAGE_MS,
  readPushPrimingPageviews,
  runPushPrimingFallback,
} from './pushPrimingRules';

function readSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * W2-B: Hat das Konto sein erstes Erlebnis (erster eigener Zug) hinter sich?
 * Lokaler Merker, Server-Zaehler aus /me und die Vorbelegung fuer
 * Bestandskonten — dieselben Quellen wie auf dem Abenteuer-Tab.
 */
function firstExperienceDone(user: Record<string, unknown> | null): boolean {
  if (hasFirstExperience()) return true;
  const count = typeof user?.own_message_count === 'number' ? user.own_message_count : 0;
  if (count > 0) return true;
  return ensureFirstExperience(user);
}

export function PushPrimingHost() {
  const { status, user } = useAuth();
  const userRef = useRef<Record<string, unknown> | null>(null);
  userRef.current = (user as Record<string, unknown> | null) ?? null;
  const location = useLocation();
  // Sitzungsbeginn im Sinne von „seit wann ist diese App offen" — bewusst der
  // Mount-Zeitpunkt und nicht der Login, damit ein Reload die 5 Minuten neu
  // startet (die Sitzungsbremse in den Regeln verhindert trotzdem eine Serie).
  const startedAtRef = useRef<number>(Date.now());
  const pageviewsRef = useRef(0);

  useEffect(() => {
    if (status !== 'authed') return undefined;
    if (typeof window === 'undefined') return undefined;
    // Harte Grenzen vorziehen: beantwortet oder abgeschaltet = gar nichts tun.
    if (isStartupPushPromptDone()) return undefined;
    if (!isProactivePushEnabled()) return undefined;

    const session = readSessionStorage();
    // Jeder Routenwechsel ist ein Seitenaufruf (dieselbe Quelle wie der
    // PageviewTracker: useLocation). Der Zaehler lebt in sessionStorage, damit
    // er einen Reload ueberlebt und pro Sitzung EINMAL die Schwelle reisst.
    // W2-B: Onboarding und Tueren zaehlen nicht (siehe pushPrimingRules).
    const pathname = location.pathname;
    pageviewsRef.current = countsAsPushPrimingPageview(pathname)
      ? countPushPrimingPageview(session)
      : readPushPrimingPageviews(session);

    const evaluate = () => {
      const outcome = runPushPrimingFallback({
        promptDone: isStartupPushPromptDone(),
        proactiveEnabled: isProactivePushEnabled(),
        elapsedMs: Date.now() - startedAtRef.current,
        pageviews: pageviewsRef.current,
        session,
        // W2-B: in der ersten Szene erst nach dem ersten eigenen Zug.
        quiet: isPushPrimingQuietPath(pathname, firstExperienceDone(userRef.current)),
      });
      if (outcome === 'fire') requestPushPriming('fallback');
    };

    // (a) Seitenzahl sofort pruefen, (b) die Zeitbedingung per Wecker.
    evaluate();
    const remaining = Math.max(
      0,
      PUSH_PRIMING_USAGE_MS - (Date.now() - startedAtRef.current),
    );
    const timer = window.setTimeout(evaluate, remaining);
    // Aufraeumen: der Wecker darf nach Abmeldung/Unmount nicht mehr feuern.
    return () => window.clearTimeout(timer);
  }, [location.pathname, status]);

  return null;
}
